import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Box, Paper, Typography, Card, CardContent, Button, TextField, Switch, FormControlLabel,
  Tabs, Tab, Chip, IconButton, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions,
  InputAdornment, Divider, Select, MenuItem, SelectChangeEvent, Alert, CircularProgress,
} from '@mui/material';
import {
  Add, ContentCopy, QrCode2, Settings, Business, Person, Payment, Notifications,
  Visibility, Edit, Delete, Download, Code,
} from '@mui/icons-material';
import { DataService } from '../services/dataService';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`settings-tabpanel-${index}`}
      aria-labelledby={`settings-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const SettingsScreen: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [showAddCodeDialog, setShowAddCodeDialog] = useState(false);
  const [showAddQRDialog, setShowAddQRDialog] = useState(false);
  const [commissionType, setCommissionType] = useState<'fixed' | 'percentage'>('percentage');
  const [commissionRate, setCommissionRate] = useState(5);
  const [bonusPerLead, setBonusPerLead] = useState(50);
  const [monthlyBonus, setMonthlyBonus] = useState(200);
  const [autoValidation, setAutoValidation] = useState(true);
  const [notifications, setNotifications] = useState(true);
  const [actionMessage, setActionMessage] = useState<string>('');
  const [isSaving, setIsSaving] = useState(false);

  const queryClient = useQueryClient();

  // Fetch real data
  const { data: sponsorCodes, isLoading: codesLoading, error: codesError } = useQuery({
    queryKey: ['sponsorCodes'],
    queryFn: DataService.getSponsorCodes,
  });

  const { data: qrCodes, isLoading: qrLoading, error: qrError } = useQuery({
    queryKey: ['qrCodes'],
    queryFn: DataService.getQRCodes,
  });

  const { data: systemConfig, isLoading: configLoading, error: configError } = useQuery({
    queryKey: ['systemConfig'],
    queryFn: DataService.getSystemConfig,
  });

  // Update local state when config loads
  React.useEffect(() => {
    if (systemConfig) {
      setCommissionType(systemConfig.commissionType);
      setCommissionRate(systemConfig.commissionRate);
      setBonusPerLead(systemConfig.bonusPerLead);
      setMonthlyBonus(systemConfig.monthlyBonus);
      setAutoValidation(systemConfig.autoValidation);
      setNotifications(systemConfig.notifications);
    }
  }, [systemConfig]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleCommissionTypeChange = (event: SelectChangeEvent) => {
    setCommissionType(event.target.value as 'fixed' | 'percentage');
  };

  const handleSaveConfiguration = async () => {
    setIsSaving(true);
    try {
      await DataService.updateSystemConfig({
        commissionType,
        commissionRate,
        bonusPerLead,
        monthlyBonus,
        autoValidation,
        notifications,
      });
      
      setActionMessage('Configuration sauvegardée avec succès!');
      setTimeout(() => setActionMessage(''), 3000);
      
      // Invalidate queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['systemConfig'] });
    } catch (error) {
      setActionMessage(`Erreur lors de la sauvegarde: ${error instanceof Error ? error.message : 'Erreur inconnue'}`);
      setTimeout(() => setActionMessage(''), 5000);
    } finally {
      setIsSaving(false);
    }
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setActionMessage('Copié dans le presse-papiers!');
      setTimeout(() => setActionMessage(''), 2000);
    } catch (error) {
      setActionMessage('Erreur lors de la copie');
      setTimeout(() => setActionMessage(''), 3000);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Paramètres & Affiliation</Typography>
      </Box>

      {/* Action Message */}
      {actionMessage && (
        <Alert 
          severity="success" 
          sx={{ mb: 3 }}
          onClose={() => setActionMessage('')}
        >
          {actionMessage}
        </Alert>
      )}

      <Paper sx={{ width: '100%' }}>
        <Tabs value={tabValue} onChange={handleTabChange} aria-label="settings tabs">
          <Tab 
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Code />
                Codes parrain
              </Box>
            } 
          />
          <Tab 
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <QrCode2 />
                Codes QR
              </Box>
            } 
          />
          <Tab 
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Settings />
                Configuration
              </Box>
            } 
          />
        </Tabs>

        {/* Sponsor Codes Tab */}
        <TabPanel value={tabValue} index={0}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6">
              Codes de parrainage
            </Typography>
            <Button variant="contained" startIcon={<Add />} onClick={() => setShowAddCodeDialog(true)}>
              Ajouter un code
            </Button>
          </Box>

          {codesLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          )}

          {codesError && (
            <Alert severity="error" sx={{ mb: 3 }}>
              Erreur lors du chargement des codes: {codesError instanceof Error ? codesError.message : 'Erreur inconnue'}
            </Alert>
          )}

          {!codesLoading && !codesError && (!sponsorCodes || sponsorCodes.length === 0) && (
            <Alert severity="info" sx={{ mb: 3 }}>
              Aucun code de parrainage trouvé. Les codes sont générés automatiquement pour chaque parrain.
            </Alert>
          )}

          {!codesLoading && !codesError && sponsorCodes && sponsorCodes.length > 0 && (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }, gap: 3 }}>
              {sponsorCodes.map((code) => (
                <Card key={code.id}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="h6">{code.code}</Typography>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title="Copier">
                          <IconButton size="small" onClick={() => copyToClipboard(code.code)}>
                            <ContentCopy />
                          </IconButton>
                        </Tooltip>
                        <IconButton size="small">
                          <Edit />
                        </IconButton>
                        <IconButton size="small" color="error">
                          <Delete />
                        </IconButton>
                      </Box>
                    </Box>
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                      Parrain: {code.sponsorName}
                    </Typography>
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                      Utilisations: {code.usageCount}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                      <Chip label={code.status} color={code.status === 'active' ? 'success' : 'default'} size="small" />
                      <Chip label={`${code.commissionRate}%`} color="primary" size="small" />
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Box>
          )}
        </TabPanel>

        {/* QR Codes Tab */}
        <TabPanel value={tabValue} index={1}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6">Codes QR</Typography>
            <Button
              variant="contained"
              startIcon={<QrCode2 />}
              onClick={() => setShowAddQRDialog(true)}
            >
              Générer un QR
            </Button>
          </Box>

          {qrLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          )}

          {qrError && (
            <Alert severity="error" sx={{ mb: 3 }}>
              Erreur lors du chargement des codes QR: {qrError instanceof Error ? qrError.message : 'Erreur inconnue'}
            </Alert>
          )}

          {!qrLoading && !qrError && (!qrCodes || qrCodes.length === 0) && (
            <Alert severity="info" sx={{ mb: 3 }}>
              Aucun code QR trouvé. Les codes QR sont générés automatiquement pour chaque parrain.
            </Alert>
          )}

          {!qrLoading && !qrError && qrCodes && qrCodes.length > 0 && (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 3 }}>
              {qrCodes.map((qr) => (
                <Card key={qr.id}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="h6">{qr.name}</Typography>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title="Copier l'URL">
                          <IconButton size="small" onClick={() => copyToClipboard(qr.url)}>
                            <ContentCopy />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Télécharger">
                          <IconButton size="small">
                            <Download />
                          </IconButton>
                        </Tooltip>
                        <IconButton size="small">
                          <Edit />
                        </IconButton>
                        <IconButton size="small" color="error">
                          <Delete />
                        </IconButton>
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                      <Box
                        sx={{
                          width: 120,
                          height: 120,
                          bgcolor: 'grey.100',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderRadius: 1,
                        }}
                      >
                        <QrCode2 sx={{ fontSize: 80, color: 'grey.400' }} />
                      </Box>
                    </Box>
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                      URL: {qr.url}
                    </Typography>
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                      Scans: {qr.scanCount}
                    </Typography>
                    <Chip label={qr.status} color={qr.status === 'active' ? 'success' : 'default'} size="small" />
                  </CardContent>
                </Card>
              ))}
            </Box>
          )}
        </TabPanel>

        {/* Configuration Tab */}
        <TabPanel value={tabValue} index={2}>
          <Typography variant="h6" gutterBottom>
            Configuration des commissions
          </Typography>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 3 }}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Type de commission
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <FormControlLabel
                  control={<Switch checked={commissionType === 'percentage'} onChange={(e) => setCommissionType(e.target.checked ? 'percentage' : 'fixed')} />}
                  label="Pourcentage (%)"
                />
                <FormControlLabel
                  control={<Switch checked={commissionType === 'fixed'} onChange={(e) => setCommissionType(e.target.checked ? 'fixed' : 'percentage')} />}
                  label="Montant fixe (€)"
                />
              </Box>
            </Paper>

            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Taux de commission
              </Typography>
              <TextField
                fullWidth
                type="number"
                label={commissionType === 'percentage' ? 'Pourcentage (%)' : 'Montant (€)'}
                value={commissionRate}
                onChange={(e) => setCommissionRate(Number(e.target.value))}
                InputProps={{
                  endAdornment: <InputAdornment position="end">{commissionType === 'percentage' ? '%' : '€'}</InputAdornment>,
                }}
              />
            </Paper>
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 3, mt: 3 }}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Bonus et récompenses
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                  fullWidth
                  type="number"
                  label="Bonus par lead (€)"
                  value={bonusPerLead}
                  onChange={(e) => setBonusPerLead(Number(e.target.value))}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">€</InputAdornment>,
                  }}
                />
                <TextField
                  fullWidth
                  type="number"
                  label="Bonus mensuel (€)"
                  value={monthlyBonus}
                  onChange={(e) => setMonthlyBonus(Number(e.target.value))}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">€</InputAdornment>,
                  }}
                />
              </Box>
            </Paper>

            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Paramètres généraux
              </Typography>
              
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 3 }}>
                <FormControlLabel
                  control={<Switch checked={autoValidation} onChange={(e) => setAutoValidation(e.target.checked)} />}
                  label="Validation automatique"
                />
                <FormControlLabel
                  control={<Switch checked={notifications} onChange={(e) => setNotifications(e.target.checked)} />}
                  label="Notifications"
                />
              </Box>
            </Paper>
          </Box>

          {/* Save Button */}
          <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
            <Button
              variant="contained"
              size="large"
              onClick={handleSaveConfiguration}
              disabled={isSaving}
              startIcon={isSaving ? <CircularProgress size={20} /> : <Settings />}
            >
              {isSaving ? 'Sauvegarde...' : 'Sauvegarder la configuration'}
            </Button>
          </Box>
        </TabPanel>
      </Paper>

      {/* Add Code Dialog */}
      <Dialog open={showAddCodeDialog} onClose={() => setShowAddCodeDialog(false)}>
        <DialogTitle>Ajouter un code de parrainage</DialogTitle>
        <DialogContent>
          <Typography>Fonctionnalité en cours de développement</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAddCodeDialog(false)}>Fermer</Button>
        </DialogActions>
      </Dialog>

      {/* Add QR Dialog */}
      <Dialog open={showAddQRDialog} onClose={() => setShowAddQRDialog(false)}>
        <DialogTitle>Générer un code QR</DialogTitle>
        <DialogContent>
          <Typography>Fonctionnalité en cours de développement</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAddQRDialog(false)}>Fermer</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SettingsScreen;
